/**
 * Sync expenses coparenting serveur ↔ local — Phase 4, incrément 4.10.3.
 *
 * Troisième sub-entité **coparenting** synchronisée. Étend ADR 0009
 * (server-wins) au champ `data.frais` des foyers coparenting.
 *
 * Symétrique de journal-sync (4.10.1) : pas de mutable state côté
 * serveur (les dépenses sont immuables — create + delete, pas
 * d'update), donc le sub-sync se contente de migrer les expenses
 * pré-auth via POST et de rapatrier l'autorité GET.
 *
 * Idempotent, filtre `home.mode === 'coparenting'`, déclenché en
 * parallèle des autres sub-syncs par `homes-sync.ts`.
 */
import * as expensesApi from '../api/expenses.js';
import { ApiError } from '../api/client.js';
import {
  applyExpensesSyncResult,
  getHomesSnapshot,
  isServerHomeId,
  type ServerExpensePayload,
} from './homes.js';
import type { CoparentingHomeData, Expense } from '@nestor/shared-types';

export interface ExpensesSyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  lastAt: number | null;
  errorCode: string | null;
}

let status: ExpensesSyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getExpensesSyncStatus(): ExpensesSyncStatus {
  return status;
}

/**
 * Synchronise les expenses pour tous les foyers UUID coparenting.
 * Idempotent.
 */
export async function syncExpensesForAllHomes(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const { homes } = getHomesSnapshot();

  let anyError = false;
  let lastErrorCode: string | null = null;

  for (const home of homes) {
    if (!isServerHomeId(home.id)) continue;
    if (home.mode !== 'coparenting') continue;

    const data = home.data as CoparentingHomeData;

    // 1. Migration POST des expenses locales pré-auth.
    // Phase 3.3.3 : offline-first via SendResult (cf. 3.3.2).
    const idMap = new Map<string, string>();
    const localOnly = data.frais.filter((e) => !isServerHomeId(e.id));
    for (const expense of localOnly) {
      try {
        const result = await expensesApi.createExpense(
          home.id,
          buildCreateInput(expense),
          expense.id,
        );
        if (result.kind === 'sent') {
          idMap.set(expense.id, result.data.expense.id);
        }
      } catch (err) {
        const code = errorCode(err);
        anyError = true;
        lastErrorCode = code;
        console.warn('[expenses-sync] migration POST échouée', {
          homeId: home.id,
          expenseId: expense.id,
          error: code,
        });
      }
    }

    // 2. Fetch + 3. apply.
    try {
      const serverExpenses = await expensesApi.listExpenses(home.id);
      const payload: ServerExpensePayload[] = serverExpenses.map((e) => ({
        id: e.id,
        description: e.description,
        amount: e.amount,
        category: e.category,
        paidBy: e.paidBy,
        date: e.date,
      }));
      applyExpensesSyncResult(home.id, { idMap, serverExpenses: payload });
    } catch (err) {
      if (idMap.size > 0) {
        applyExpensesSyncResult(home.id, { idMap });
      }
      const code = errorCode(err);
      anyError = true;
      lastErrorCode = code;
      console.warn('[expenses-sync] fetch /expenses échoué', { homeId: home.id, error: code });
    }
  }

  status = anyError
    ? { state: 'error', lastAt: Date.now(), errorCode: lastErrorCode }
    : { state: 'ok', lastAt: Date.now(), errorCode: null };
}

function buildCreateInput(expense: Expense): expensesApi.CreateExpenseInput {
  return {
    description: expense.description,
    amount: expense.amount,
    category: expense.category,
    paidBy: expense.paidBy,
    date: expense.date,
  };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/** @internal pour tests — reset le state module-level. */
export function _resetExpensesSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
